<script setup>
import '@campact/camper/icons/CmprIconSolidPen';

import { computed } from 'vue';
import TlsNotice from './TlsNotice.vue';

const emit = defineEmits(['tryOpen']);

const props = defineProps({
  number: {
    type: Number,
    default: undefined,
  },
  opened: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: undefined,
  },
  tlsNotice: {
    type: String,
    default: undefined,
  },
});

const closed = computed(() => !props.opened);
const showTlsNotice = computed(() => props.tlsNotice !== undefined && props.opened);

function onClick() {
  if (closed.value) emit('tryOpen');
}
</script>

<template>
  <div class="step-container">
    <div
      class="step"
      :class="[opened ? 'step--opened' : 'step--closed']"
    >
      <div
        class="step__header"
        @click.prevent="onClick"
      >
        <div class="step__number">
          {{ number }}
        </div>
        <div class="step__title">
          {{ title }}
        </div>
      </div>

      <div
        v-if="opened"
        class="step__body"
      >
        <slot name="opened" />
      </div>

      <div
        v-if="closed"
        class="step__summary"
        @click.prevent="onClick"
      >
        <slot name="closed" />
      </div>
    </div>

    <tls-notice v-if="showTlsNotice">
      {{ props.tlsNotice }}
    </tls-notice>
  </div>
</template>

<style scoped lang="scss">
@import '@cta/frontend/stylesheets/custom-media';

.step-container {
  margin-bottom: var(--cmpr-space-90);
}

.step {
  --step-background-color: var(--cmpr-color-neutral-20);
  --step-border-width: var(--cmpr-border-width-20);
  --step-padding: var(--cmpr-space-50) var(--cmpr-space-50) var(--cmpr-space-60);
  --step-header-number-color: var(--cmpr-color-white);
  --step-header-number-background-color: var(--cmpr-color-secondary-80);
  --step-header-number-size: 24px;
  --step-header-number-border-color: transparent;
  --step-header-number-border-width: 2px;
  --step-header-title-color: var(--cmpr-color-secondary-80);
  --step-header-title-font-size: var(--cmpr-font-size-60);
  --step-header-title-font-weight: var(--cmpr-font-weight-30);
  --step-header-title-font-family: var(--cmpr-font-family-headline);

  background-color: var(--step-background-color);
  border: var(--step-border-width) solid var(--cmpr-color-neutral-30);
  margin-bottom: var(--cmpr-space-50);
  padding: var(--step-padding);

  @media (--cmpr-breakpoint-desktop-min) {
    --step-padding: var(--cmpr-space-60) var(--cmpr-space-60) var(--cmpr-space-70);
  }

  &--closed {
    --step-background-color: transparent;
    --step-border-width: 0;
    --step-padding: 0;
    --step-header-title-color: var(--cmpr-color-neutral-50);
    --step-header-title-font-size: var(--cmpr-font-size-50);
    --step-header-title-font-weight: var(--cmpr-font-weight-20);
    --step-header-title-font-family: var(--cmpr-font-family-body);
    --step-header-number-color: var(--cmpr-color-neutral-50);
    --step-header-number-background-color: transparent;
    --step-header-number-border-color: var(--step-header-title-color);

    cursor: pointer;
  }
}

.step__header {
  display: grid;
  gap: 8px;
  grid-template-columns: var(--step-header-number-size) auto;
  line-height: 27px;
  margin-bottom: 16px;
}

.step__number {
  background-color: var(--step-header-number-background-color);
  border: var(--step-header-number-border-width) solid var(--step-header-number-border-color);
  border-radius: 50%;
  box-sizing: border-box;
  color: var(--step-header-number-color);
  display: block;
  font-size: var(--cmpr-font-size-30);
  font-weight: var(--cmpr-font-weight-30);
  height: var(--step-header-number-size);
  line-height: calc(var(--step-header-number-size) - 2 * var(--step-header-number-border-width));
  margin-top: var(--cmpr-space-10);
  text-align: center;
  width: var(--step-header-number-size);
}

.step__title {
  color: var(--step-header-title-color);
  font-family: var(--step-header-title-font-family);
  font-size: var(--step-header-title-font-size);
  font-weight: var(--step-header-title-font-weight);
}
</style>
